import React from 'react';
import { useForm } from 'react-hook-form';
import { Alert, Box, Checkbox, Text } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import type { CheckedChangeDetails } from '@zag-js/checkbox';
import { ReactComponent as InputErrorIcon } from 'assets/icons/input_error.svg';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';

import { Button, Input } from 'components';
import { yup } from 'utils';
import { emailRegex } from 'utils/regex';

import CustomerAgreement from '../CustomerAgreement';

type PayloadType = { email: string; agreement: boolean };
/* eslint-disable no-unused-vars */
type FormType = {
  onButton: (data: PayloadType) => void;
  isDisableForm?: boolean;
  counter: number;
  defaultEmail?: string;
  isSubmitting?: boolean;
};
const Form: React.FC<FormType> = ({
  onButton,
  isDisableForm = false,
  counter,
  defaultEmail,
  isSubmitting = false,
}: FormType) => {
  const { t } = useTranslation('component.auth.signUp.form');

  const schema = yup.object().shape({
    email: yup
      .string()
      .email()
      .lowercase()
      .matches(emailRegex, t('email_validate'))
      .label('email')
      .required(),
    agreement: yup
      .boolean()
      .label('agreement')
      .oneOf([true], t('checkAgreement'))
      .required(),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      agreement: false,
    },
  });

  const onSubmit = async (data: PayloadType) => {
    onButton(data);
  };

  const formProps = useInputAttributes(register, errors, {
    email: t('email'),
  });

  const resendButton = `${t('resend')} ( ${counter.toString()} )`;

  React.useEffect(() => {
    if (defaultEmail) {
      setValue('email', defaultEmail);
    }
  }, [defaultEmail, setValue]);

  const fieldSurface = {
    background: 'secondary',
    borderWidth: '1px',
    borderColor: 'rgba(255,255,255,0.08)',
    borderRadius: 'lg',
    _hover: { background: 'rgba(255,255,255,0.05)' },
    _focus: {
      background: 'secondary',
      borderColor: 'primary',
    },
  };

  return (
    <Box w="100%">
      {isDisableForm && (
        <Alert.Root
          status="info"
          mb={5}
          borderRadius="md"
          borderWidth="1px"
          borderColor="rgba(255,255,255,0.12)"
        >
          <Alert.Indicator />
          <Text color="info">{t('codeSent')}</Text>
        </Alert.Root>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box mb={5}>
          <Input
            {...formProps.email}
            disabled={isDisableForm || Boolean(defaultEmail) || isSubmitting}
            {...fieldSurface}
          />
        </Box>
        <Checkbox.Root
          {...formProps.agreement}
          disabled={isDisableForm || isSubmitting}
          colorPalette="whiteAlpha"
          onCheckedChange={(d: CheckedChangeDetails) =>
            setValue('agreement', d.checked === true)
          }
          mt={2}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control>
            <Checkbox.Indicator />
          </Checkbox.Control>
          <Checkbox.Label>
            <CustomerAgreement />
            {errors?.agreement && (
              <Box pos="absolute" top={6} display="flex" alignItems="center">
                <InputErrorIcon />
                <Text color="error" pl={2} fontSize="xs">
                  {errors.agreement.message}
                </Text>
              </Box>
            )}
          </Checkbox.Label>
        </Checkbox.Root>
        <Button
          type="submit"
          variant="filled"
          display="block"
          title={isDisableForm ? resendButton : t('submit')}
          isDisabled={isDisableForm || isSubmitting}
          isLoading={isSubmitting}
          minW="100%"
          mt={8}
        />
      </form>
    </Box>
  );
};

export default Form;
